import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaShoppingCart, FaHeart, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { FiHeart } from "react-icons/fi";
import { motion } from "framer-motion";
import api from "../api";

export default function FeaturedProducts() {
  const navigate = useNavigate();
  const sliderRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [favorites, setFavorites] = useState({});
  const [selectedSizes, setSelectedSizes] = useState({});
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(null);
  const [toast, setToast] = useState("");
  const [typedTitle, setTypedTitle] = useState("");

  const title = "Featured Rugs";
  const creamLight = "#FBF8F1";
  const cream = "#F5EFE3";
  const teal = "#0F766E";
  const tealDark = "#115E59";
  const brown = "#8B5E3C";

  useEffect(() => {
    let mounted = true;
    async function fetchProducts() {
      try {
        const res = await api.get("/api/products/");
        const data = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.results)
            ? res.data.results
            : [];
        if (!mounted) return;
        setProducts(data);
        const initial = {};
        data.forEach((product) => {
          if (Array.isArray(product.sizes) && product.sizes.length) {
            initial[product.id] = product.sizes[0].id;
          }
        });
        setSelectedSizes(initial);
        try {
          setFavorites(JSON.parse(localStorage.getItem("favs") || "{}"));
        } catch {
          setFavorites({});
        }
      } catch (error) {
        console.error("Error fetching featured products:", error);
        if (mounted) setProducts([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchProducts();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    setTypedTitle("");
    let index = 0;
    const timer = setInterval(() => {
      index += 1;
      setTypedTitle(title.slice(0, index));
      if (index >= title.length) clearInterval(timer);
    }, 85);
    return () => clearInterval(timer);
  }, []);

  const showToast = (message) => {
    setToast(message);
    window.clearTimeout(showToast.timeout);
    showToast.timeout = window.setTimeout(() => setToast(""), 2500);
  };

  const getSizes = (product) => Array.isArray(product?.sizes) ? product.sizes : [];

  const getSelectedSize = (product) => {
    const sizes = getSizes(product);
    if (!sizes.length) return null;
    return sizes.find((size) => String(size.id) === String(selectedSizes[product.id])) || sizes[0];
  };

  const getPricing = (product) => {
    const size = getSelectedSize(product);
    if (!size) return { original: null, sale: null, discount: 0, discounted: false };

    const original = Number(size.price);
    const discountPrice = Number(size.discount_price);
    const hasOriginal = Number.isFinite(original) && original > 0;
    const hasDiscount = Number.isFinite(discountPrice) && discountPrice > 0;

    if (hasOriginal && hasDiscount && discountPrice < original) {
      return {
        original,
        sale: discountPrice,
        discount: Math.round(((original - discountPrice) / original) * 100),
        discounted: true,
      };
    }

    if (!hasOriginal && hasDiscount) {
      return { original: null, sale: discountPrice, discount: 0, discounted: false };
    }

    return {
      original: hasOriginal ? original : null,
      sale: hasOriginal ? original : null,
      discount: 0,
      discounted: false,
    };
  };

  const scrollProducts = (direction) => {
    const slider = sliderRef.current;
    if (!slider) return;

    const firstCard = slider.querySelector("[data-featured-card]");
    if (!firstCard) return;

    const cardWidth = firstCard.getBoundingClientRect().width;
    const computedStyle = window.getComputedStyle(slider);
    const gap = parseFloat(computedStyle.columnGap || computedStyle.gap || "0") || 0;

    // Move exactly one visible column. Because the grid has two rows,
    // each horizontal step advances by two products.
    slider.scrollBy({
      left: direction * (cardWidth + gap),
      behavior: "smooth",
    });
  };

  const toggleFavorite = async (product, event) => {
    event.stopPropagation();
    const localFavs = JSON.parse(localStorage.getItem("favs") || "{}");
    const next = !localFavs[product.id];
    localFavs[product.id] = next;
    localStorage.setItem("favs", JSON.stringify(localFavs));
    setFavorites({ ...localFavs });

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user?.access) return;
    try {
      await api.post("/api/favorites/toggle/", { product_id: product.id }, {
        headers: { Authorization: `Bearer ${user.access}` },
      });
    } catch (error) {
      console.error("Favorite error:", error);
    }
  };

  const addToCart = async (product, event) => {
    event.stopPropagation();
    if (product.is_sold) {
      showToast("This rug has been sold.");
      return;
    }

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user?.access) {
      showToast("Please login first.");
      return;
    }

    const sizes = getSizes(product);
    const selectedSize = getSelectedSize(product);
    if (sizes.length && !selectedSizes[product.id]) {
      showToast("Please select a size.");
      return;
    }

    setAddingToCart(product.id);
    try {
      await api.post("/api/carts/add/", {
        product_id: product.id,
        size_id: selectedSize?.id || null,
        quantity: 1,
      }, {
        headers: {
          Authorization: `Bearer ${user.access}`,
          "Content-Type": "application/json",
        },
      });
      window.dispatchEvent(new Event("cartUpdated"));
      showToast("Added to cart");
    } catch (error) {
      console.error("Add to cart error:", error);
      showToast(error.response?.data?.detail || error.response?.data?.error || "Unable to add this product to cart.");
    } finally {
      setAddingToCart(null);
    }
  };

  if (loading) {
    return (
      <section className="py-10 sm:py-12 md:py-16" style={{ backgroundColor: creamLight }}>
        <div className="mx-auto max-w-7xl px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="mb-8 text-center">
            <div className="mx-auto h-8 w-48 animate-pulse rounded-lg" style={{ backgroundColor: cream }} />
            <div className="mx-auto mt-3 h-4 w-64 animate-pulse rounded-lg" style={{ backgroundColor: cream }} />
          </div>
          <div className="grid grid-flow-col grid-rows-2 auto-cols-[calc(50%-4px)] gap-2 overflow-hidden sm:auto-cols-[calc(50%-8px)] sm:gap-4 md:auto-cols-[calc(33.333%-14px)] md:gap-5 lg:auto-cols-[calc(25%-15px)]">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="min-w-0 animate-pulse overflow-hidden rounded-xl sm:rounded-2xl" style={{ backgroundColor: cream }}>
                <div className="aspect-[4/3] bg-[#E7DCC9]" />
                <div className="space-y-2 p-3"><div className="h-4 rounded bg-[#E7DCC9]" /><div className="h-3 w-2/3 rounded bg-[#E7DCC9]" /></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-8 sm:py-10 md:py-14" style={{ backgroundColor: creamLight }}>
      {toast && <div className="fixed left-5 right-5 top-5 z-50 rounded-xl px-5 py-3 text-center font-medium text-white shadow-xl sm:left-auto" style={{ backgroundColor: teal }}>{toast}</div>}

      <div className="mx-auto max-w-7xl px-3 sm:px-4 md:px-6 lg:px-8">
        <div className="mb-7 flex items-center justify-between sm:mb-9 md:mb-10">
          <div className="flex-1 text-center">
            <div className="inline-flex min-h-[42px] items-center text-2xl font-bold sm:text-3xl md:text-4xl" style={{ color: tealDark }}>
              <span>{typedTitle}</span>
              <span className="ml-1 inline-block h-7 w-[2px] animate-pulse bg-[#0F766E] sm:h-8 md:h-9" aria-hidden="true" />
            </div>
            <p className="mt-2 text-xs sm:mt-3 sm:text-sm md:text-base" style={{ color: brown }}>Discover rugs selected for character, craftsmanship, and timeless style</p>
          </div>

          {products.length > 8 && (
            <div className="absolute right-3 flex gap-2 sm:right-4 md:right-6 lg:right-8">
              <button type="button" onClick={() => scrollProducts(-1)} aria-label="Previous featured rugs" className="flex h-9 w-9 items-center justify-center rounded-full border bg-white shadow-sm transition hover:scale-105 sm:h-10 sm:w-10" style={{ borderColor: "#D8CBB7", color: tealDark }}>
                <FaChevronLeft className="text-xs sm:text-sm" />
              </button>
              <button type="button" onClick={() => scrollProducts(1)} aria-label="Next featured rugs" className="flex h-9 w-9 items-center justify-center rounded-full border bg-white shadow-sm transition hover:scale-105 sm:h-10 sm:w-10" style={{ borderColor: "#D8CBB7", color: tealDark }}>
                <FaChevronRight className="text-xs sm:text-sm" />
              </button>
            </div>
          )}
        </div>

        {products.length === 0 ? (
          <div className="py-16 text-center text-gray-500">No products found</div>
        ) : (
          <div
            ref={sliderRef}
            className="grid grid-flow-col grid-rows-2 auto-cols-[calc(50%-4px)] gap-2 overflow-x-auto overflow-y-hidden scroll-smooth pb-4 snap-x snap-mandatory scrollbar-none sm:auto-cols-[calc(50%-8px)] sm:gap-4 md:auto-cols-[calc(33.333%-14px)] md:gap-5 lg:auto-cols-[calc(25%-15px)]"
            style={{ scrollbarWidth: "none" }}
          >
            {products.map((product, index) => {
              const sizes = getSizes(product);
              const selectedSize = getSelectedSize(product);
              const pricing = getPricing(product);
              const image = product?.sub_images?.[0]?.image || product?.image || "/placeholder.jpg";

              return (
                <motion.article
                  key={product.id}
                  data-featured-card
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.08 }}
                  transition={{ duration: 0.4, delay: Math.min(index * 0.04, 0.25) }}
                  className="group min-w-0 w-full snap-start overflow-hidden rounded-xl border shadow-sm transition-all duration-500 hover:-translate-y-1 hover:shadow-xl sm:rounded-2xl"
                  style={{ backgroundColor: cream, borderColor: "#E7DCC9" }}
                >
                  <div className="relative aspect-[4/3] cursor-pointer overflow-hidden" onClick={() => navigate(`/product/${product.id}`)}>
                    <img src={image} alt={product.name || "Handmade Afghan rug"} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    {product.is_sold && <div className="absolute inset-0 flex items-center justify-center bg-black/45"><span className="rounded-full bg-white px-4 py-2 text-sm font-bold text-gray-900">SOLD</span></div>}
                    {pricing.discounted && !product.is_sold && <span className="absolute left-2 top-2 rounded-full bg-[#0F766E] px-2.5 py-1 text-[10px] font-bold text-white sm:left-3 sm:top-3 sm:text-xs">{pricing.discount}% OFF</span>}
                    <button type="button" aria-label={favorites[product.id] ? "Remove from favorites" : "Add to favorites"} onClick={(event) => toggleFavorite(product, event)} className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 shadow-md transition hover:scale-105 sm:right-3 sm:top-3 sm:h-10 sm:w-10">
                      {favorites[product.id] ? <FaHeart className="text-red-500" /> : <FiHeart className="text-gray-700" />}
                    </button>
                  </div>

                  <div className="p-3 sm:p-4">
                    <button type="button" onClick={() => navigate(`/product/${product.id}`)} className="block w-full text-left">
                      <h3 className="line-clamp-2 text-sm font-semibold sm:text-base" style={{ color: tealDark }}>{product.name}</h3>
                    </button>
                    <p className="mt-1 truncate text-[10px] text-gray-400 sm:text-xs">{product.product_id || `RUG-${product.id}`}</p>

                    {sizes.length > 0 && (
                      <select value={selectedSizes[product.id] || ""} onClick={(event) => event.stopPropagation()} onChange={(event) => setSelectedSizes((previous) => ({ ...previous, [product.id]: event.target.value }))} className="mt-3 w-full rounded-lg border bg-white px-2 py-2 text-xs outline-none transition focus:ring-2 focus:ring-[#0F766E]/20 sm:text-sm" style={{ borderColor: "#D8CDBD" }}>
                        <option value="">Select size</option>
                        {sizes.map((size) => <option key={size.id} value={size.id}>{size.length_ft || size.length_cm} × {size.width_ft || size.width_cm}</option>)}
                      </select>
                    )}

                    <div className="mt-3 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        {pricing.discounted && <p className="text-[10px] text-gray-400 line-through sm:text-xs">${pricing.original.toFixed(2)}</p>}
                        <p className="text-base font-bold sm:text-lg" style={{ color: teal }}>
                          {pricing.sale !== null ? `$${pricing.sale.toFixed(2)}` : "Price unavailable"}
                        </p>
                      </div>

                      <button type="button" disabled={product.is_sold || addingToCart === product.id} onClick={(event) => addToCart(product, event)} aria-label={product.is_sold ? "Sold" : "Add to cart"} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:scale-105 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-40" style={{ backgroundColor: teal }}>
                        {addingToCart === product.id ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" /> : <FaShoppingCart className="text-sm" />}
                      </button>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        )}

        {products.length > 0 && (
          <div className="mt-8 text-center">
            <button type="button" onClick={() => navigate("/products/All")} className="rounded-full border border-[#0F766E] px-6 py-2.5 text-sm font-semibold text-[#0F766E] transition hover:bg-[#0F766E] hover:text-white">View all rugs</button>
          </div>
        )}
      </div>
    </section>
  );
}
